import React, { useState, useCallback } from "react";
import { css } from "@emotion/react";
import {
  Button,
  Heading,
} from "@phoenix/components";

// ==========================================
// Crisp SVGs for Icons
// ==========================================

export const MCPProviderIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#7c3aed"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
    <path d="M18 10v4" />
    <path d="m15 12 3 2 3-2" />
  </svg>
);

const FormatAlignIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="15" y2="12" />
    <line x1="3" y1="18" x2="18" y2="18" />
  </svg>
);

const CodeBracketsIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const ChevronDownIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ==========================================
// Types & Defaults
// ==========================================

export interface MCPTargetConfigValues {
  serverConfig: string;
  configJson: string;
  testGenerationInstructions: string;
  delayMs: number | string;
  extensionHook: string;
  prompts: string[];
}

export const DEFAULT_MCP_TARGET_CONFIG: MCPTargetConfigValues = {
  serverConfig: "mcp",
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const MCP_EXAMPLE_CONFIG = {
  serverPath: "./mcp-server",
  args: [
    "--port",
    "3000",
  ],
};

export const MCP_EXAMPLES_LIST = [
  {
    code: "mcp:my-server",
    desc: "Connect to MCP server.",
  },
  {
    code: "mcp:filesystem",
    desc: "Filesystem MCP server.",
  },
];

// ==========================================
// Formatting & Validation Helpers
// ==========================================

export function formatMCPConfigJson(jsonStr: string): { formatted?: string; error?: string } {
  if (!jsonStr || !jsonStr.trim()) {
    return { formatted: "{}" };
  }
  try {
    const parsed = JSON.parse(jsonStr);
    return { formatted: JSON.stringify(parsed, null, 2) };
  } catch (err: any) {
    return { error: "Invalid JSON: " + (err?.message || "Syntax error") };
  }
}

export function validateMCPTargetConfig(values: MCPTargetConfigValues): {
  isValid: boolean;
  errors: {
    serverConfig?: string;
    configJson?: string;
    prompts?: string;
  };
} {
  const errors: {
    serverConfig?: string;
    configJson?: string;
    prompts?: string;
  } = {};

  if (!values.serverConfig || !values.serverConfig.trim()) {
    errors.serverConfig = "MCP Server Configuration is required";
  }

  if (values.configJson && values.configJson.trim()) {
    try {
      JSON.parse(values.configJson);
    } catch (err: any) {
      errors.configJson = "Invalid JSON configuration: " + (err?.message || "Syntax error");
    }
  }

  if (!values.prompts || values.prompts.length === 0 || values.prompts.every((p) => !p.trim())) {
    errors.prompts = "At least one prompt is required";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

// ==========================================
// Component Props
// ==========================================

export interface MCPTargetConfigurationProps {
  targetName?: string;
  values: MCPTargetConfigValues;
  onChange: (updates: Partial<MCPTargetConfigValues>) => void;
  onBack: () => void;
  onNext: () => void;
  hideBottomBar?: boolean;
  renderModeSwitcher?: React.ReactNode;
}

// ==========================================
// Styles
// ==========================================

const pageWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const pageHeaderTitle = css`
  margin: 0;
  font-size: 1.45rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const providerHeaderRow = css`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const providerBadgeTitle = css`
  font-size: 1.18rem;
  font-weight: 750;
  color: #0f172a;

  .theme--dark & {
    color: #f1f5f9 !important;
  }
`;

const formCardContainer = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
  }
`;

const formGroup = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const fieldLabel = css`
  font-size: 0.92rem;
  font-weight: 750;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 4px;

  .theme--dark & {
    color: #f1f5f9 !important;
  }
`;

const textInput = (hasError?: boolean) => css`
  width: 100%;
  height: 42px;
  padding: 0 14px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #0f172a;
  background: #ffffff;
  border: 1.5px solid ${hasError ? "#ef4444" : "#cbd5e1"};
  border-radius: 8px;
  outline: none;
  transition: all 0.15s ease-in-out;

  &:focus {
    border-color: ${hasError ? "#ef4444" : "#0f766e"};
    box-shadow: 0 0 0 3px ${hasError ? "rgba(239, 68, 68, 0.12)" : "rgba(15, 118, 110, 0.12)"};
  }

  &::placeholder {
    color: #94a3b8;
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: ${hasError ? "#ef4444" : "#475569"} !important;
  }
`;

const fieldHelpText = css`
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.45;

  a {
    color: #0f766e;
    text-decoration: underline;
    font-weight: 600;
    &:hover {
      color: #0d9488;
    }
  }

  .theme--dark & {
    color: #94a3b8 !important;
    a {
      color: #2dd4bf !important;
    }
  }
`;

const errorMessageText = css`
  margin: 0;
  font-size: 0.82rem;
  color: #ef4444;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const jsonHeaderRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const formatButton = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 0.84rem;
  font-weight: 650;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #94a3b8;
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #475569 !important;
    &:hover {
      background: #334155 !important;
      color: #ffffff !important;
    }
  }
`;

const examplesBox = css`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;

  .theme--dark & {
    border-color: #334155 !important;
    background: #1e293b !important;
  }
`;

const examplesTrigger = (isOpen: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  background: ${isOpen ? "#f1f5f9" : "#f8fafc"};
  border: none;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 650;
  color: #475569;
  text-align: left;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  .theme--dark & {
    background: ${isOpen ? "#334155" : "#1e293b"} !important;
    color: #cbd5e1 !important;
    &:hover {
      background: #334155 !important;
      color: #ffffff !important;
    }
  }
`;

const examplesContent = css`
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 14px;

  .theme--dark & {
    border-color: #334155 !important;
    background: #0f172a !important;
  }
`;

const exampleSectionTitle = css`
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 6px;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const exampleList = css`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const exampleListItem = css`
  font-size: 0.84rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;

  pre {
    background: #f1f5f9;
    color: #0f766e;
    padding: 4px 8px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.78rem;
    font-weight: 600;
    border: 1px solid #e2e8f0;
    margin: 0;
    white-space: pre-wrap;
  }

  .theme--dark & {
    color: #cbd5e1 !important;
    pre {
      background: #1e293b !important;
      color: #2dd4bf !important;
      border-color: #334155 !important;
    }
  }
`;

const jsonEditorContainer = (hasError?: boolean) => css`
  border: 1.5px solid ${hasError ? "#ef4444" : "#e2e8f0"};
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  transition: border-color 0.15s ease-in-out;

  .theme--dark & {
    border-color: ${hasError ? "#ef4444" : "#334155"} !important;
    background: #0f172a !important;
  }
`;

const jsonEditorHeaderBar = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.78rem;
  font-weight: 750;
  color: #64748b;
  letter-spacing: 0.04em;

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #94a3b8 !important;
  }
`;

const jsonTextarea = css`
  width: 100%;
  min-height: 120px;
  padding: 12px 14px;
  font-family: "Geist Mono", Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #0f172a;
  background: #ffffff;
  border: none;
  outline: none;
  resize: vertical;

  &::placeholder {
    color: #94a3b8;
  }

  .theme--dark & {
    background: #0f172a !important;
    color: #f8fafc !important;
  }
`;

const collapsibleCard = css`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
  transition: all 0.18s ease-in-out;

  .theme--dark & {
    border-color: #334155 !important;
    background: #0f172a !important;
  }
`;

const collapsibleTriggerButton = (isOpen: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  background: ${isOpen ? "#f8fafc" : "#ffffff"};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: #f8fafc;
  }

  .theme--dark & {
    background: ${isOpen ? "#1e293b" : "#0f172a"} !important;
    &:hover {
      background: #1e293b !important;
    }
  }
`;

const collapsibleTitle = css`
  margin: 0;
  font-size: 0.98rem;
  font-weight: 750;
  color: #0f172a;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const collapsibleSubtitle = css`
  margin: 3px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const collapsibleBody = css`
  padding: 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: #ffffff;

  .theme--dark & {
    border-color: #334155 !important;
    background: #0f172a !important;
  }
`;

const promptsSection = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const promptsHeader = css`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 750;
  color: #0f172a;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const promptItemRow = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const promptTextarea = css`
  flex: 1;
  padding: 10px 14px;
  font-family: inherit;
  font-size: 0.92rem;
  line-height: 1.5;
  color: #0f172a;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  resize: vertical;
  transition: all 0.15s ease-in-out;

  &:focus {
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
  }

  &::placeholder {
    color: #94a3b8;
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: #475569 !important;
  }
`;

const trashButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover:not(:disabled) {
    border-color: #ef4444;
    color: #ef4444;
    background: #fef2f2;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #64748b !important;
    &:hover:not(:disabled) {
      border-color: #ef4444 !important;
      color: #ef4444 !important;
      background: #450a0a !important;
    }
  }
`;

const addPromptButton = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  align-self: flex-start;
  font-size: 0.88rem;
  font-weight: 700;
  color: #0f172a;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: #475569 !important;
    &:hover {
      background: #334155 !important;
    }
  }
`;

const bottomNavRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
`;

// ==========================================
// Main Component
// ==========================================

export function MCPTargetConfiguration({
  targetName = "support-agent",
  values,
  onChange,
  onBack,
  onNext,
  hideBottomBar = false,
  renderModeSwitcher,
}: MCPTargetConfigurationProps) {
  // Collapsible accordion states
  const [examplesExpanded, setExamplesExpanded] = useState(false);
  const [testGenExpanded, setTestGenExpanded] = useState(false);
  const [delayExpanded, setDelayExpanded] = useState(false);
  const [extHookExpanded, setExtHookExpanded] = useState(false);

  // Field validation and format errors
  const [errors, setErrors] = useState<{
    serverConfig?: string;
    configJson?: string;
    prompts?: string;
  }>({});
  const [jsonFormatError, setJsonFormatError] = useState<string | null>(null);

  const handleFormatJson = useCallback(() => {
    const res = formatMCPConfigJson(values.configJson);
    if (res.error) {
      setJsonFormatError(res.error);
    } else if (res.formatted) {
      onChange({ configJson: res.formatted });
      setJsonFormatError(null);
    }
  }, [values.configJson, onChange]);

  const handleLoadExampleJson = useCallback(() => {
    onChange({ configJson: JSON.stringify(MCP_EXAMPLE_CONFIG, null, 2) });
    setJsonFormatError(null);
    setExamplesExpanded(false);
  }, [onChange]);

  // Prompt actions
  const handlePromptChange = (index: number, newText: string) => {
    const updated = [...values.prompts];
    updated[index] = newText;
    onChange({ prompts: updated });
    if (errors.prompts) setErrors((prev) => ({ ...prev, prompts: undefined }));
  };

  const handleAddPrompt = () => {
    onChange({ prompts: [...values.prompts, "{{prompt}}"] });
  };

  const handleRemovePrompt = (index: number) => {
    if (values.prompts.length <= 1) return;
    const updated = values.prompts.filter((_, i) => i !== index);
    onChange({ prompts: updated });
  };

  const handleNextClick = () => {
    const result = validateMCPTargetConfig(values);
    if (!result.isValid) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    onNext();
  };

  return (
    <div css={pageWrapperStyle}>
      {/* 1. Header Section */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", marginBottom: "8px" }}>
        <div>
          <Heading level={1} css={pageHeaderTitle}>
            {`Configure Target: ${targetName}`}
          </Heading>
          <div css={providerHeaderRow}>
            <MCPProviderIcon />
            <span css={providerBadgeTitle}>MCP Provider</span>
          </div>
        </div>
        {renderModeSwitcher && (
          <div style={{ flexShrink: 0, marginTop: "4px" }}>
            {renderModeSwitcher}
          </div>
        )}
      </div>

      {/* 2. Main Configuration Form Card */}
      <div css={formCardContainer}>
        {/* Field 1: MCP Server Configuration */}
        <div css={formGroup}>
          <label css={fieldLabel} htmlFor="mcp-server-config-input">
            MCP Server Configuration <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            id="mcp-server-config-input"
            type="text"
            css={textInput(!!errors.serverConfig)}
            placeholder="mcp"
            value={values.serverConfig}
            onChange={(e) => {
              onChange({ serverConfig: e.target.value });
              if (errors.serverConfig) {
                setErrors((prev) => ({ ...prev, serverConfig: undefined }));
              }
            }}
          />
          {errors.serverConfig && (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {errors.serverConfig}
            </p>
          )}
          <p css={fieldHelpText}>
            MCP connects to compatible servers for tool use and context management.
          </p>
        </div>

        {/* Section 2: Configuration (JSON) */}
        <div css={formGroup}>
          <div css={jsonHeaderRow}>
            <label css={fieldLabel} htmlFor="mcp-config-json">
              Configuration (JSON)
            </label>
            <button
              type="button"
              css={formatButton}
              onClick={handleFormatJson}
              title="Format JSON with 2-space indentation"
            >
              <FormatAlignIcon />
              <span>Format</span>
            </button>
          </div>

          {/* Examples Collapsible */}
          <div css={examplesBox}>
            <button
              type="button"
              css={examplesTrigger(examplesExpanded)}
              onClick={() => setExamplesExpanded(!examplesExpanded)}
              aria-expanded={examplesExpanded}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CodeBracketsIcon />
                <span>Examples</span>
              </span>
              <ChevronDownIcon
                style={{
                  transform: examplesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.18s ease-in-out",
                }}
              />
            </button>

            {examplesExpanded && (
              <div css={examplesContent}>
                <div>
                  <div css={exampleSectionTitle}>MCP Provider Examples</div>
                  <ul css={exampleList}>
                    {MCP_EXAMPLES_LIST.map((item, idx) => (
                      <li key={idx} css={exampleListItem}>
                        <pre>{item.code}</pre>
                        <span>{item.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div css={exampleSectionTitle}>JSON Config Example</div>
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      padding: "10px 12px",
                      fontSize: "0.82rem",
                      fontFamily: "monospace",
                      color: "#334155",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <pre style={{ margin: 0 }}>
                      {JSON.stringify(MCP_EXAMPLE_CONFIG, null, 2)}
                    </pre>
                    <button
                      type="button"
                      onClick={handleLoadExampleJson}
                      style={{
                        alignSelf: "flex-start",
                        padding: "3px 8px",
                        fontSize: "0.78rem",
                        fontWeight: 650,
                        background: "#2563eb",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Use This Example
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* JSON Editor Box */}
          <div css={jsonEditorContainer(!!jsonFormatError || !!errors.configJson)}>
            <div css={jsonEditorHeaderBar}>
              <span>JSON</span>
            </div>
            <textarea
              id="mcp-config-json"
              css={jsonTextarea}
              placeholder="{}"
              value={values.configJson}
              onChange={(e) => {
                onChange({ configJson: e.target.value });
                if (jsonFormatError) setJsonFormatError(null);
                if (errors.configJson) {
                  setErrors((prev) => ({ ...prev, configJson: undefined }));
                }
              }}
              spellCheck={false}
            />
          </div>

          {jsonFormatError || errors.configJson ? (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {jsonFormatError || errors.configJson}
            </p>
          ) : (
            <p css={fieldHelpText}>
              MCP server path and arguments.
            </p>
          )}
        </div>

        {/* Section 3: Test Generation (Collapsible) */}
        <div css={collapsibleCard}>
          <button
            type="button"
            css={collapsibleTriggerButton(testGenExpanded)}
            onClick={() => setTestGenExpanded(!testGenExpanded)}
            aria-expanded={testGenExpanded}
          >
            <div>
              <h3 css={collapsibleTitle}>Test Generation</h3>
              <p css={collapsibleSubtitle}>
                Configure how red team test cases are generated for this target
              </p>
            </div>
            <ChevronDownIcon
              style={{
                transform: testGenExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease-in-out",
                color: "#64748b",
              }}
            />
          </button>

          {testGenExpanded && (
            <div css={collapsibleBody}>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="mcp-test-gen-instructions-input">
                  Instructions
                </label>
                <p css={fieldHelpText}>
                  Additional guidance for generating red team attacks. Useful if you need to tweak
                  anything from structure to specific scenarios for your application.
                </p>
                <textarea
                  id="mcp-test-gen-instructions-input"
                  css={textInput()}
                  style={{ minHeight: "80px", height: "auto", padding: "10px 14px", resize: "vertical" }}
                  placeholder="Generate the attack as a news article"
                  value={values.testGenerationInstructions}
                  onChange={(e) => onChange({ testGenerationInstructions: e.target.value })}
                />
                <details style={{ marginTop: "6px", fontSize: "0.85rem", color: "#64748b" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>View examples</summary>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "18px", lineHeight: "1.6" }}>
                    <li>Generate the attack as a news article</li>
                    <li>Generate the attack as a receipt or invoice</li>
                    <li>Limit the attack to 300 characters</li>
                    <li>Adopt a frustrated customer persona when attacking</li>
                    <li>
                      Format the attack as a JSON object: <code>{`{"message": "..."}`}</code>
                    </li>
                  </ul>
                </details>
              </div>

              <div css={formGroup}>
                <label css={fieldLabel}>Generate Multiple Variables</label>
                <p css={fieldHelpText}>
                  Define variables that will be generated alongside adversarial prompts. Use{" "}
                  <code>{"{{variable_name}}"}</code> in your request body, headers, or URL.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section 4: Delay (Collapsible) */}
        <div css={collapsibleCard}>
          <button
            type="button"
            css={collapsibleTriggerButton(delayExpanded)}
            onClick={() => setDelayExpanded(!delayExpanded)}
            aria-expanded={delayExpanded}
          >
            <div>
              <h3 css={collapsibleTitle}>Delay</h3>
              <p css={collapsibleSubtitle}>Configure the delay between requests</p>
            </div>
            <ChevronDownIcon
              style={{
                transform: delayExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease-in-out",
                color: "#64748b",
              }}
            />
          </button>

          {delayExpanded && (
            <div css={collapsibleBody}>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="mcp-delay-ms-input">
                  Delay (ms)
                </label>
                <input
                  id="mcp-delay-ms-input"
                  type="number"
                  min="0"
                  css={textInput()}
                  placeholder="0"
                  value={values.delayMs}
                  onChange={(e) => onChange({ delayMs: Number(e.target.value) || 0 })}
                />
                <p css={fieldHelpText}>
                  Delay between each request sent to the target in milliseconds.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section 5: Extension Hook (Collapsible) */}
        <div css={collapsibleCard}>
          <button
            type="button"
            css={collapsibleTriggerButton(extHookExpanded)}
            onClick={() => setExtHookExpanded(!extHookExpanded)}
            aria-expanded={extHookExpanded}
          >
            <div>
              <h3 css={collapsibleTitle}>Extension Hook</h3>
              <p css={collapsibleSubtitle}>
                Run custom code at specific points in the evaluation lifecycle
              </p>
            </div>
            <ChevronDownIcon
              style={{
                transform: extHookExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease-in-out",
                color: "#64748b",
              }}
            />
          </button>

          {extHookExpanded && (
            <div css={collapsibleBody}>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="mcp-extension-hook-input">
                  Extension Hook Path
                </label>
                <input
                  id="mcp-extension-hook-input"
                  type="text"
                  css={textInput()}
                  placeholder="/path/to/hook.py:hook_function"
                  value={values.extensionHook}
                  onChange={(e) => onChange({ extensionHook: e.target.value })}
                />
                <p css={fieldHelpText}>
                  Path to an extension file and optional function name to run custom lifecycle
                  hooks.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section 6: Prompts */}
        <div css={promptsSection}>
          <h2 css={promptsHeader}>Prompts</h2>
          <p css={fieldHelpText}>
            Enter your prompts below. Use{" "}
            <code
              style={{
                background: "#f1f5f9",
                padding: "2px 6px",
                borderRadius: "4px",
                fontFamily: "monospace",
                color: "#0f766e",
                fontWeight: 650,
              }}
            >
              {"{{ prompt }}"}
            </code>{" "}
            as a placeholder where you want the user's input to appear in your prompt template.
          </p>

          {values.prompts.map((promptText, index) => (
            <div key={index} css={formGroup}>
              <label css={fieldLabel} htmlFor={`mcp-prompt-input-${index}`}>
                {values.prompts.length === 1 ? "Prompt" : `Prompt ${index + 1}`}
              </label>
              <div css={promptItemRow}>
                <textarea
                  id={`mcp-prompt-input-${index}`}
                  css={promptTextarea}
                  rows={3}
                  placeholder="{{prompt}}"
                  value={promptText}
                  onChange={(e) => handlePromptChange(index, e.target.value)}
                />
                <button
                  type="button"
                  css={trashButton}
                  onClick={() => handleRemovePrompt(index)}
                  title={`Remove prompt ${index + 1}`}
                  aria-label={`Remove prompt ${index + 1}`}
                  disabled={values.prompts.length <= 1}
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))}

          {errors.prompts && (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {errors.prompts}
            </p>
          )}

          <button type="button" css={addPromptButton} onClick={handleAddPrompt}>
            <PlusIcon />
            <span>Add Prompt</span>
          </button>
        </div>
      </div>

      {/* 3. Bottom Navigation Bar */}
      {!hideBottomBar && (
        <div css={bottomNavRow}>
          <Button
            variant="default"
            type="button"
            style={{
              padding: "9px 22px",
              fontSize: "0.92rem",
              fontWeight: 650,
              borderRadius: "8px",
              border: "1.5px solid #cbd5e1",
              color: "#475569",
              background: "#ffffff",
            }}
            onClick={onBack}
          >
            &lt; Back
          </Button>
          <Button
            type="button"
            style={{
              padding: "9px 24px",
              fontSize: "0.95rem",
              fontWeight: 750,
              borderRadius: "8px",
              background: "#2563eb",
              color: "#ffffff",
              border: "1px solid #1d4ed8",
              cursor: "pointer",
            }}
            onClick={handleNextClick}
          >
            Next &gt;
          </Button>
        </div>
      )}
    </div>
  );
}
